import { Fragment, useCallback, useEffect, useId, useMemo, useRef, useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useBlocker } from "react-router-dom";
import { Drawer } from "../Drawer";
import { PageFormShell } from "../ui/PageFormShell";
import { DateField } from "../ui/DateField";
import { DocHeader } from "../ui/DocHeader";
import { todayISO } from "../../lib/trDate";
import { Icon } from "../ui/Icon";
import { SearchSelect, type SSOption } from "../ui/SearchSelect";
import { trpc } from "../../lib/trpc";
import { MaterialNeeds, MaterialNeedsKapi, type MaterialNeedsVeri } from "./MaterialNeeds";
import { PriceAgreementDrawer } from "../agreements/PriceAgreementDrawer";
import { useIsMobile } from "../../lib/useIsMobile";
import { ConfirmDialog } from "../ui/ConfirmDialog";
import "./OrderEditor.css";

const CUR_SYMBOL: Record<string, string> = { TRY: "₺", USD: "$", EUR: "€", GBP: "£" };

type OrderType = "customer" | "stock_replenish";

interface LineRow {
  itemId: string;
  qty: string;
  uomId: string;
  // Düzenlemede yüklenen mevcut satır bilgisi — dondurulmuş fiyatı korumak için (senaryo: anlaşma sonradan silinse de düzenleme kilitlenmesin).
  origItemId?: string; // yüklenen özgün ürün (dondurulmuş fiyat yalnız ürün DEĞİŞMEDİYSE geçerli)
  itemName?: string; // yüklenen ürün adı (uyarı mesajı — products listesinde olmasa da)
  frozen?: { unitPrice: number; currency: string; exchangeRate: number | null } | null; // sipariş satırının dondurulmuş fiyatı
}

interface AgreementInitial {
  unitPrice: number;
  currency: string;
  exchangeRate: number | null;
}

interface OrderEditorProps {
  open: boolean;
  /** Verilirse düzenleme modu — mevcut sipariş yüklenir, kaydet order.update çağırır. */
  orderId?: string | null;
  onClose: () => void;
  /**
   * "page" → uygulama içi SEKME olarak çizilir (PageFormShell); "drawer" (varsayılan) →
   * çekmece. Gövde AYNI kalır; yalnız sarmalayıcı değişir (ItemFormModal ile aynı desen).
   *
   * YENİ sipariş sekmeden açılır: kullanıcı 20 satırlık siparişi doldururken stok/reçete
   * ekranına bakıp geri dönebilsin — çekmecede sayfadan çıkmak girişi siliyordu.
   * DÜZENLEME çekmece kalır: mevcut siparişe hızlı müdahale, listeden kopmadan.
   */
  variant?: "drawer" | "page";
}

function fmtNum(n: number): string {
  return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 2 }).format(n);
}

// Seçili ürün için canlı ATP rozeti
function AtpBadge({ itemId, requestedQty, detay = false }: {
  itemId: string;
  requestedQty: number;
  /**
   * Dökümü METİN olarak da bas. Masaüstünde döküm `title` ipucunda duruyor; dokunmatik
   * ekranda `title` HİÇ görünmez → "stokta 2.700 var ama müsait 0" durumunun açıklamasına
   * mobilde ulaşılamıyordu (aşağıdaki yorumun uyardığı şaşkınlık tam olarak buydu).
   */
  detay?: boolean;
}) {
  const atpQ = trpc.order.atp.useQuery({ itemKind: "finished", itemId }, { enabled: !!itemId, staleTime: 10_000 });
  if (!itemId) return null;
  if (atpQ.isLoading) return <span className="oe-note">ATP…</span>;
  const a = atpQ.data;
  if (!a) return null;
  const enough = a.available >= requestedQty;
  // Renk TEK BAŞINA anlam taşımaz: yetmeyen durumda metin "N eksik → WO" da der.
  const durum = requestedQty > 0 ? (enough ? " ok" : " short") : "";
  return (
    <span
      className={`oe-atp${durum}`}
      // "müsait", stokta duran ama BAŞKA siparişe üretilmiş (earmark'lı) malı içermez — o mal
      // vaat edilemez. İpucu farkı açıkça yazar, yoksa "stokta 2.700 var ama müsait 0" şaşırtır.
      title={
        `Stokta ${fmtNum(a.onHand)}` +
        (a.earmarked > 0 ? `, başka siparişe ayrılmış ${fmtNum(a.earmarked)}` : "") +
        `, rezerve ${fmtNum(a.reserved)}`
      }
    >
      müsait {fmtNum(a.available)}
      {requestedQty > 0 && !enough ? ` · ${fmtNum(requestedQty - a.available)} eksik → WO` : ""}
      {detay ? (
        <span className="oe-atp-detay">
          stokta {fmtNum(a.onHand)}
          {a.earmarked > 0 ? ` · başka siparişe ayrılmış ${fmtNum(a.earmarked)}` : ""}
          {` · rezerve ${fmtNum(a.reserved)}`}
        </span>
      ) : null}
    </span>
  );
}

// Satır için son anlaşma fiyatı — SALT-OKUNUR. Anlaşma varsa "Düzenle" (revize)
// butonu, yoksa "Anlaşma Yap". Fiyat elle değiştirilmez; revize anlaşmadan yapılır.
function LineAgreement({ customerId, itemId, frozen, onResolved, onOpen }: {
  customerId: string;
  itemId: string;
  frozen?: { unitPrice: number; currency: string; exchangeRate: number | null } | null; // sipariş satırının dondurulmuş fiyatı (güncel anlaşma yoksa gösterilir)
  /** `price` = satırda GÖSTERİLEN etkin fiyat (anlaşma ya da dondurulmuş), yoksa null. */
  onResolved: (customerId: string, itemId: string, hasPrice: boolean, price: { unitPrice: number; currency: string } | null) => void;
  onOpen: (itemId: string, initial: AgreementInitial | null) => void;
}) {
  const q = trpc.priceAgreement.latest.useQuery(
    { customerId, itemKind: "finished", itemId },
    { enabled: !!customerId && !!itemId },
  );
  useEffect(() => {
    if (!customerId || !itemId) return;
    if (q.data === undefined) return;
    /**
     * ETKİN FİYAT yukarı bildirilir: güncel anlaşma, yoksa satırın DONDURULMUŞ fiyatı.
     * Toplam kutusu bu değerden hesaplanır — ekranda görünen rakamla toplanan rakam AYNI
     * kaynaktan gelsin diye. Toplam ayrıca sorgulansaydı iki yer sessizce ayrışabilirdi.
     */
    const etkin = q.data
      ? { unitPrice: q.data.unitPrice, currency: q.data.currency }
      : (frozen ? { unitPrice: frozen.unitPrice, currency: frozen.currency } : null);
    onResolved(customerId, itemId, q.data !== null, etkin);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.data, customerId, itemId, frozen?.unitPrice, frozen?.currency]);
  if (!customerId || !itemId) return null;
  if (q.isLoading) return <span className="oe-note">fiyat…</span>;
  const a = q.data;
  if (!a) {
    // Güncel anlaşma yok — ama sipariş satırının DONDURULMUŞ fiyatı varsa onu göster (düzenleme kilitlenmez).
    if (frozen) {
      return (
        <span className="oe-agr" title="Sipariş oluşturulurken dondurulan fiyat — güncel anlaşma yok">
          Fiyat: <b>{fmtNum(frozen.unitPrice)} {CUR_SYMBOL[frozen.currency] ?? frozen.currency}</b>
          <span className="muted">· dondurulmuş</span>
          <button type="button" className="btn-md secondary oe-agr-btn" onClick={() => onOpen(itemId, null)} title="Yeni anlaşma yap (güncel fiyat)">
            <Icon name="plus" size={11} />Anlaşma Yap
          </button>
        </span>
      );
    }
    return (
      <span className="oe-agr">
        <span className="oe-agr-none"><Icon name="alertTriangle" size={12} />Anlaşma yok</span>
        <button type="button" className="btn-md secondary oe-agr-btn" onClick={() => onOpen(itemId, null)}>
          <Icon name="plus" size={11} />Anlaşma Yap
        </button>
      </span>
    );
  }
  return (
    <span className="oe-agr" title={a.exchangeRate != null ? `Kur: ${a.exchangeRate} ₺` : undefined}>
      Fiyat: <b>{fmtNum(a.unitPrice)} {CUR_SYMBOL[a.currency] ?? a.currency}</b>
      {a.exchangeRate != null ? <span className="muted">· kur {fmtNum(a.exchangeRate)}₺</span> : null}
      <button type="button" className="iact oe-agr-edit" aria-label="Anlaşmayı düzenle" title="Anlaşmayı düzenle" onClick={() => onOpen(itemId, { unitPrice: a.unitPrice, currency: a.currency, exchangeRate: a.exchangeRate })}>
        <Icon name="edit" size={13} />
      </button>
    </span>
  );
}

const emptyLine = (uomId: string): LineRow => ({ itemId: "", qty: "", uomId });

/** Form imzası — "kaydedilmemiş değişiklik var mı" karşılaştırması için. Fiyat/anlaşma durumu
 *  sunucudan türediği için imzaya GİRMEZ; yalnız kullanıcının yazdıkları girer. */
function imzala(o: { orderType: OrderType; customerId: string; requestedDate: string; promisedDate: string; notes: string; lines: LineRow[] }): string {
  return JSON.stringify([
    o.orderType, o.customerId, o.requestedDate, o.promisedDate, o.notes.trim(),
    o.lines.map((l) => [l.itemId, l.qty, l.uomId]),
  ]);
}

/**
 * ═══ MOBİLDE KAYDEDİLMEMİŞ SİPARİŞİ KORU ═══
 *
 * Masaüstünde yeni sipariş SEKMEDE canlı tutulur (TabViews keep-alive): başka sekmeye geçmek
 * formu silmez. Mobil kabukta sekme YOK — `<Outlet/>` sayfayı söker. Alt çubuktaki beş kısayoldan
 * birine, menüye ya da bildirime tek dokunuş, 10 satırlık yarım siparişi SESSİZCE siliyordu.
 * Android'in geri tuşu da aynı yoldan gelir.
 *
 * Bu bileşen YALNIZ mobilde ve form açıkken MONTE EDİLİR. Kanca koşulsuz çağrılsaydı masaüstünde
 * her açık editör bir engelleyici kaydederdi: yönlendirici aynı anda tek engelleyici destekler,
 * sekmelerdeki iki editör birbirini ezerdi ve her gezinmede konsol uyarısı basılırdı.
 */
function KayitsizCikisKorumasi({ kirli, kasitli }: { kirli: boolean; kasitli: { current: boolean } }) {
  const blocker = useBlocker(({ currentLocation, nextLocation }) =>
    kirli && !kasitli.current && currentLocation.pathname !== nextLocation.pathname,
  );
  return (
    <ConfirmDialog
      open={blocker.state === "blocked"}
      tone="danger"
      title="Sipariş kaydedilmedi"
      message="Bu ekrandan ayrılırsanız girdiğiniz sipariş bilgileri silinir. Yine de ayrılmak istiyor musunuz?"
      confirmLabel="Ayrıl"
      cancelLabel="Ekranda kal"
      onConfirm={() => blocker.proceed?.()}
      onCancel={() => blocker.reset?.()}
    />
  );
}

/** Değeri gecikmeli döndürür — hızlı miktar girişinde sunucuya sürekli sorgu atmamak için. */
function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/**
 * Canlı malzeme ihtiyacı — henüz KAYDEDİLMEMİŞ satırlar için.
 *
 * Gösterim ve hesap paylaşılan: tablo `MaterialNeeds` bileşeni, hesap `order.materialNeeds`
 * (→ `mrp.ts` runMrp, sipariş kapsamı). Sipariş detayındaki kart AYNI ikisini kullanır —
 * aynı sipariş iki ekranda farklı okunamaz.
 *
 * KAPI: hesap İSTEK ÜZERİNE başlar (`MaterialNeedsKapi`) — sipariş detayındaki kartla aynı
 * kural. Kapı olmadan, mevcut bir siparişi düzenlemek için çekmece açıldığında satırlar
 * kendiliğinden dolduğu için kullanıcı hiçbir şeye DOKUNMADAN tam bir MRP koşusu başlıyordu;
 * debounce yalnız tuş başına sorguyu engeller, duraklama başına sorguyu engellemez.
 * Kapı bir kez açıldıktan sonra kart CANLIDIR: satır anahtarı geciktirilerek (450 ms)
 * her düzenlemede yeniden hesaplanır.
 */
function RawMaterialNeeds({ lines, orderId }: { lines: Array<{ itemId: string; qty: number }>; orderId: string | null }) {
  const [calistir, setCalistir] = useState(false);
  const key = lines.map((l) => `${l.itemId}:${l.qty}`).join("|");
  const debKey = useDebounced(key, 450);
  const debLines = useMemo(
    () => (debKey ? debKey.split("|").map((s) => { const [itemId = "", qtyStr = ""] = s.split(":"); return { itemId, qty: Number(qtyStr) }; }).filter((l) => l.itemId && l.qty > 0) : []),
    [debKey],
  );
  /**
   * KAYITLI sipariş düzenleniyorsa `orderId` DE gönderilir: talep düzenlenen satırlardan gelir,
   * ama zaten SEVK EDİLEN/ÜRETİLEN miktar siparişten düşülür. Gönderilmezse aynı sipariş
   * detayda "kalan 60 koli", düzenleme çekmecesinde "100 koli" okunur ve iki ekran iki farklı
   * satın alma kararı verir (uçtaki ayna notu tam olarak bunu anlatıyor).
   */
  const q = trpc.order.materialNeeds.useQuery(
    orderId ? { orderId, lines: debLines } : { lines: debLines },
    { enabled: calistir && debLines.length > 0, staleTime: 5_000 },
  );
  // Hesaplanacak geçerli satır yok → kart hiç görünmez (gösterilecek bir soru yok).
  if (lines.length === 0) return null;
  if (!calistir) return <MaterialNeedsKapi onCalistir={() => setCalistir(true)} />;
  return (
    <MaterialNeeds
      veri={q.data satisfies MaterialNeedsVeri | undefined}
      yukleniyor={q.isLoading}
      hata={q.error?.message}
    />
  );
}

export function OrderEditorDrawer({ open, orderId, onClose, variant = "drawer" }: OrderEditorProps) {
  const qc = useQueryClient();
  const isEdit = !!orderId;
  const Shell = variant === "page" ? PageFormShell : Drawer;
  /**
   * DOM ID'LERİ BENZERSİZ OLMAK ZORUNDA.
   *
   * Sekme kabuğu tüm açık sekmeleri AYNI ANDA mount tutuyor. Sabit `id="order-form"` ile
   * hem sekmedeki yeni sipariş formu hem listedeki düzenleme çekmecesi açıkken alt
   * şeritteki `form={fid("form")}` düğmesi belgede İLK bulduğu forma bağlanır — kullanıcı
   * "Oluştur"a basar, GİZLİ formdaki veri gönderilir ya da hiçbir şey olmaz. Aynı hata
   * kart formunda (ItemFormModal) ölçülerek bulunmuştu.
   */
  const uid = useId();
  const fid = (k: string) => `${uid}-${k}`;
  const isPage = variant === "page";
  /**
   * MOBİL (≤767px): kalem TABLOSU yerine kalem KARTLARI.
   * 7 sütunlu tablo ~742px; 375px'lik ekranda kabuk onu yalnız YATAY KAYDIRABİLİYORDU —
   * ürün seçici, miktar ve sil düğmesi ekranın farklı yerlerinde, kullanıcı her satırda
   * sağa-sola kaydırarak çalışıyordu. Proje deseni (Üretim, Sevkiyat, Siparişler…):
   * dar ekranda tablo → kart. Denetimler AYNI yardımcıdan (`satir`) gelir; yalnız yerleşim değişir.
   */
  const isMobile = useIsMobile();
  /** "Kalem Ekle"den sonra kaydırılacak kart — mobilde yeni kart katlanmanın altında kalıp
   *  "düğmeye bastım, bir şey olmadı" izlenimi veriyordu. */
  const [odakSatir, setOdakSatir] = useState<number | null>(null);
  /** Kullanıcı "İptal"/"Kapat" ile BİLEREK çıkıyor — koruma sormaz (masaüstüyle aynı davranış). */
  const kasitliCikis = useRef(false);
  /**
   * Formun AÇILDIĞI andaki imzası. "Kirli" = şu anki imza bundan farklı. Düzenlemede satırlar
   * yüklenince form dolu gelir; "satır var mı" diye bakmak her düzenlemeyi kirli sayardı.
   */
  const ilkImza = useRef<string | null>(null);

  /** Belge künyesi (kim / ne zaman / hangi tesis) — yalnız sayfa yerleşiminde gösterilir. */
  const meQ = trpc.auth.me.useQuery(undefined, { staleTime: 5 * 60_000, enabled: open && isPage });
  const me = meQ.data?.user ?? null;
  const olusturan = me ? `${me.firstName ?? ""} ${me.lastName ?? ""}`.trim() : "";
  // Aktif tesis adı: kullanıcının seçili tesisi (yoksa kendi tesisi) — künye satırında.
  const aktifTesis = me
    ? (me.facilities.find((f) => f.id === (me.activeFacilityId ?? me.tesisId))?.name ?? null)
    : null;
  const populatedRef = useRef<string | null>(null);
  const [orderType, setOrderType] = useState<OrderType>("customer");
  const [customerId, setCustomerId] = useState("");
  const [requestedDate, setRequestedDate] = useState("");
  const [promisedDate, setPromisedDate] = useState("");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState<LineRow[]>([]);
  const [submitErr, setSubmitErr] = useState<string | null>(null);
  const [doneCode, setDoneCode] = useState<string | null>(null);
  // itemId → anlaşma fiyatı çözüldü mü. Müşteri siparişi her satırda anlaşma ister.
  const [priceMap, setPriceMap] = useState<Record<string, boolean>>({});
  /** itemId → satırda GÖSTERİLEN etkin birim fiyat. Toplam kutusunun TEK kaynağı. */
  const [priceInfo, setPriceInfo] = useState<Record<string, { unitPrice: number; currency: string } | null>>({});
  const [agr, setAgr] = useState<{ itemId: string; initial: AgreementInitial | null } | null>(null);
  /**
   * Düzenlenen siparişin YÜKLENDİĞİ andaki müşterisi. Dondurulmuş fiyat O müşterinin fiyatıdır:
   * düzenlemede müşteri değiştirilip yeni müşterinin anlaşması yoksa, eski müşterinin dondurulmuş
   * fiyatı yeni müşterinin siparişine elle fiyat olarak yazılıyordu (inceleme, doğrulandı).
   */
  const [yuklenenMusteri, setYuklenenMusteri] = useState<string | null>(null);
  /** Kayıt başarılı, liste tazeleniyor — düğmeler kilitli, çıkış koruması susturulmuş. */
  const [kaydedildi, setKaydedildi] = useState(false);
  /** Her gönderim denemesi — aynı hata ikinci kez oluşunca da bant görünür alana gelsin/duyurulsun. */
  const [gonderimNo, setGonderimNo] = useState(0);

  /**
   * ⚠ FİYAT HARİTALARI `müşteri:ürün` ANAHTARLI — yalnız ürün DEĞİL.
   * Eskiden anahtar yalnız üründü. Müşteri A seçiliyken X "fiyatlı" çözülüyor; müşteri B'ye
   * geçilince B'nin sorgusu yüklenene dek (hata verirse SÜRESİZ) A'nın sonucu okunuyordu: Kaydet
   * açık, ara toplamda A'nın fiyatı, gönderimde fiyat yok → sunucu B için anlaşma bulamayıp satırı
   * hata vermeden FİYATSIZ kaydediyordu (çekişmeli inceleme, 27 Eyl 2026 — doğrulandı).
   * Müşteri değişince haritayı SİLMEK çözüm değil: React'te çocuk efektleri ebeveynden önce çalışır;
   * önbellekteki yeni fiyatı çocuk yazar, ebeveynin sıfırlaması onu siler ve düğme kilitli kalırdı.
   * Anahtar müşteriyi taşıyınca eski müşterinin sonucu yeni müşteride hiç OKUNMAZ.
   */
  const fk = (itemId: string) => `${customerId}:${itemId}`;
  const onPriceResolved = useCallback((musteri: string, itemId: string, hasPrice: boolean, price: { unitPrice: number; currency: string } | null) => {
    const k = `${musteri}:${itemId}`;
    setPriceMap((m) => (m[k] === hasPrice ? m : { ...m, [k]: hasPrice }));
    // Aynı değerle yeniden set ETME: her satır kendi sorgusundan tetikleniyor, referans
    // değişimi sonsuz render döngüsü açardı.
    setPriceInfo((m) => {
      const cur = m[k] ?? null;
      if (cur?.unitPrice === price?.unitPrice && cur?.currency === price?.currency) return m;
      return { ...m, [k]: price };
    });
  }, []);

  const customersQ = trpc.customer.list.useQuery({ page: 1, pageSize: 100 }, { enabled: open });
  // Aranabilir müşteri seçici (ad = etiket, kod = mono tail — ikisi de aranır).
  const customerOptions = useMemo<SSOption[]>(
    () => (customersQ.data?.rows ?? []).map((c) => ({ value: c.id, label: c.name, tail: c.code })),
    [customersQ.data],
  );
  // Mamul seçici: TÜM aktif mamuller (v_items) — pagination YOK. (Eski finishedProduct.list pageSize:100
  // cap'liydi; >100 mamulde liste eksik kalırdı.) producibles baseUomId'yi de döndürür (otomatik birim).
  const produciblesQ = trpc.recipe.producibles.useQuery(undefined, { enabled: open, staleTime: 60_000 });
  const uomQ = trpc.uom.list.useQuery(undefined, { enabled: open, staleTime: 5 * 60_000 });
  const orderQ = trpc.order.get.useQuery({ id: orderId! }, { enabled: open && !!orderId });

  const createMut = trpc.order.create.useMutation();
  const updateMut = trpc.order.update.useMutation();

  const adetUom = uomQ.data?.find((u) => u.code === "adet");
  // Yalnız mamuller (kind === "finished"); baseUomId otomatik birim seçimi için korunur.
  const products = useMemo(
    () => ((produciblesQ.data ?? []) as Array<{ kind: string; id: string; code: string; name: string; baseUomId: string | null }>)
      .filter((p) => p.kind === "finished"),
    [produciblesQ.data],
  );
  // Aranabilir mamul seçici için seçenekler — ad = etiket, kod = mono tail (ikisi de aranır).
  const productOptions = useMemo<SSOption[]>(
    () => products.map((p) => ({ value: p.id, label: p.name, tail: p.code })),
    [products],
  );

  useEffect(() => {
    if (!open) { populatedRef.current = null; return; }
    if (isEdit) {
      // Düzenleme: order.get yüklenince BİR KEZ doldur (invalidate'te ezme).
      const od = orderQ.data;
      if (!od) return;
      if (populatedRef.current === orderId) return;
      populatedRef.current = orderId ?? null;
      setOrderType(od.orderType);
      setCustomerId(od.customerId ?? "");
      setYuklenenMusteri(od.customerId ?? "");
      setRequestedDate(od.requestedDate ? new Date(od.requestedDate).toISOString().slice(0, 10) : "");
      setPromisedDate(od.promisedDate ? new Date(od.promisedDate).toISOString().slice(0, 10) : "");
      setNotes(od.notes ?? "");
      const yuklenen = od.lines.length ? od.lines.map((l) => ({
        itemId: l.itemId, qty: String(l.qty), uomId: l.uomId,
        origItemId: l.itemId, itemName: l.itemName,
        frozen: l.unitPrice != null ? { unitPrice: l.unitPrice, currency: l.currency ?? "TRY", exchangeRate: l.exchangeRate } : null,
      })) : [emptyLine(adetUom?.id ?? "")];
      setLines(yuklenen);
      kasitliCikis.current = false;
      ilkImza.current = imzala({
        orderType: od.orderType, customerId: od.customerId ?? "",
        requestedDate: od.requestedDate ? new Date(od.requestedDate).toISOString().slice(0, 10) : "",
        promisedDate: od.promisedDate ? new Date(od.promisedDate).toISOString().slice(0, 10) : "",
        notes: od.notes ?? "", lines: yuklenen,
      });
      setSubmitErr(null); setDoneCode(null); setPriceMap({}); setPriceInfo({}); setAgr(null); setKaydedildi(false);
      createMut.reset(); updateMut.reset();
    } else {
      // İstenen tarih VARSAYILAN olarak işlem günü — siparişlerin ezici çoğunluğu bugün
      // için açılıyor; boş bırakmak her kullanıcıya aynı tıklamayı tekrarlatıyordu.
      // Kullanıcı değiştirebilir, temizleyebilir (alan zorunlu değil).
      setOrderType("customer"); setCustomerId(""); setRequestedDate(todayISO()); setPromisedDate(""); setNotes("");
      setLines([emptyLine(adetUom?.id ?? "")]); setSubmitErr(null); setDoneCode(null);
      kasitliCikis.current = false;
      ilkImza.current = imzala({
        orderType: "customer", customerId: "", requestedDate: todayISO(), promisedDate: "", notes: "",
        lines: [emptyLine(adetUom?.id ?? "")],
      });
      setPriceMap({}); setPriceInfo({}); setAgr(null); setYuklenenMusteri(null); setKaydedildi(false);
      createMut.reset(); updateMut.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit, orderId, orderQ.data, adetUom?.id]);

  const validLines = lines.filter((l) => l.itemId && l.qty && Number(l.qty) > 0 && l.uomId);
  // Satır "fiyatlı" mı: mevcut anlaşma var VEYA (düzenlemede) ürün DEĞİŞMEDEN dondurulmuş fiyatı var.
  // İkincisi kritik: sipariş oluşturulduğunda fiyat donduruldu; anlaşma sonradan silinse bile düzenleme kilitlenmez.
  /** Dondurulmuş fiyat geçerli mi: ürün DEĞİŞMEDİ ve müşteri de DEĞİŞMEDİ. */
  const donukGecerli = (l: LineRow) => !!l.frozen && l.itemId === l.origItemId && customerId === yuklenenMusteri;
  const linePriced = (l: LineRow) => priceMap[fk(l.itemId)] === true || donukGecerli(l);
  const allPriced = orderType !== "customer" || validLines.every(linePriced);
  // Anlaşması EKSİK geçerli satırların ürün adları — kullanıcı hangi satırı düzelteceğini kaydırmadan görsün.
  // (fiyatlı DEĞİL + fiyat çözümü === false). priceMap === undefined → henüz YÜKLENİYOR (eksik değil).
  const unpricedNames = orderType === "customer"
    ? lines
        .map((l, i) => ({ l, i }))
        .filter(({ l }) => l.itemId && l.qty && Number(l.qty) > 0 && l.uomId && !linePriced(l) && priceMap[fk(l.itemId)] === false)
        .map(({ l, i }) => l.itemName ?? products.find((p) => p.id === l.itemId)?.name ?? `${i + 1}. satır`)
    : [];
  const pricesLoading = orderType === "customer" && validLines.some((l) => !linePriced(l) && priceMap[fk(l.itemId)] === undefined);

  /** Satırın etkin birim fiyatı — tabloda ve toplamda AYNI değer kullanılır. */
  const lineUnitPrice = (l: LineRow) => priceInfo[fk(l.itemId)] ?? null;
  const lineTotal = (l: LineRow) => {
    const p = lineUnitPrice(l);
    const q = Number(l.qty);
    return p && Number.isFinite(q) ? { amount: p.unitPrice * q, currency: p.currency } : null;
  };
  /**
   * ARA TOPLAM — PARA BİRİMİ BAZINDA.
   *
   * TRY + USD TOPLANMAZ: bu depoda tekrarlanan bir kural (bordro, mesai ücreti, sevkiyat
   * hepsinde aynı). Tek bir "genel toplam" basmak, farklı para birimlerini toplayarak
   * anlamsız bir rakam üretirdi; her para birimi kendi satırında durur.
   */
  const totalsByCurrency = useMemo(() => {
    const m = new Map<string, number>();
    for (const l of validLines) {
      const t = lineTotal(l);
      if (!t) continue;
      m.set(t.currency, (m.get(t.currency) ?? 0) + t.amount);
    }
    return [...m.entries()].map(([currency, amount]) => ({ currency, amount }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines, priceInfo, customerId]);
  /** Fiyatı çözülememiş geçerli satır sayısı — toplam "eksik" olduğunda ilan edilir. */
  const fiyatsizSatir = validLines.filter((l) => !lineTotal(l)).length;

  /**
   * BELGE DURUM AKIŞI — gerçek `OrderStatus` değerleri (uydurulmuş adım YOK).
   * Yeni belgede ilk adım etkin; düzenlemede siparişin kendi durumu işaretlenir.
   */
  const AKIS: Array<{ key: string; label: string }> = [
    { key: "draft", label: "Taslak" },
    { key: "confirmed", label: "Onaylı" },
    { key: "in_production", label: "Üretimde" },
    { key: "ready", label: "Sevke Hazır" },
    { key: "shipped", label: "Sevk Edildi" },
  ];
  const aktifDurum = (isEdit ? orderQ.data?.status : "draft") ?? "draft";
  const customerName = (customersQ.data?.rows ?? []).find((c) => c.id === customerId)?.name;
  const agrProduct = agr ? products.find((p) => p.id === agr.itemId) : undefined;
  const pending = createMut.isPending || updateMut.isPending || kaydedildi;
  const kirli = ilkImza.current != null && !doneCode &&
    imzala({ orderType, customerId, requestedDate, promisedDate, notes, lines }) !== ilkImza.current;
  /** İptal / Kapat / × — bilerek çıkış; koruma bunu sormaz. */
  const kapat = () => { kasitliCikis.current = true; onClose(); };

  useEffect(() => {
    if (odakSatir == null) return;
    setOdakSatir(null);
    if (!isMobile) return;
    const el = document.getElementById(fid(`mline-${odakSatir}`));
    if (!el) return;
    const azHareket = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    el.scrollIntoView({ block: "center", behavior: azHareket ? "auto" : "smooth" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [odakSatir, isMobile]);

  const hataRef = useRef<HTMLDivElement>(null);
  const errorMessage = useMemo(() => {
    if (submitErr) return submitErr;
    const e = createMut.error ?? updateMut.error;
    if (!e) return null;
    const zod = (e.data as { zodError?: { fieldErrors?: Record<string, string[]> } } | undefined)?.zodError?.fieldErrors;
    if (zod) return Object.values(zod).flat().filter(Boolean).join(" · ");
    return e.message;
  }, [submitErr, createMut.error, updateMut.error]);
  /**
   * HATA GÖRÜNÜR OLMALI. Bant formun EN ÜSTÜNDE; mobilde form 2-3 ekran uzunluğunda ve
   * "Taslak Kaydet" en altta. Kullanıcı Kaydet'e basıyor, "Müşteri seçin." ekranın dışında
   * yazılıyor — hiçbir şey olmamış gibi görünüyordu. Hata değişince bant görünür alana alınır.
   */
  useEffect(() => {
    if (!errorMessage) return;
    /**
     * BİR KARE SONRA, ANINDA. Bant formun ÜSTÜNE eklenir ve tarayıcının kaydırma çapası
     * (scroll anchoring) görünen içeriği yerinde tutmak için kaydırma değerini düzeltir
     * (375px'te +55px, ölçüldü). Kaydırma o düzeltme yerleştikten sonra yapılır ki hedef
     * kaymasın; animasyonsuz, çünkü hata mesajında animasyon değil görünürlük önemli ve
     * yumuşak kaydırmanın çapa düzeltmesiyle çakışıp hedefe varmaması riskine girilmez.
     * Doğrulama: 375×812'de bant topbar'ın hemen altına (üst 53px) geliyor.
     */
    const kare = requestAnimationFrame(() => hataRef.current?.scrollIntoView({ block: "start", behavior: "auto" }));
    return () => cancelAnimationFrame(kare);
    // `gonderimNo`: aynı mesaj ikinci kez oluşunca `errorMessage` değişmez, ama kullanıcı yeniden
    // aşağıda ve bant ekran dışında olabilir — her denemede yeniden görünür alana alınır.
  }, [errorMessage, gonderimNo]);

  function setLine(idx: number, patch: Partial<LineRow>) {
    setLines((ls) => ls.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  }

  // Ürün seçilince birimi otomatik o ürünün baz birimine ayarla.
  function pickProduct(idx: number, itemId: string) {
    const prod = products.find((p) => p.id === itemId);
    setLine(idx, { itemId, ...(prod?.baseUomId ? { uomId: prod.baseUomId } : {}) });
  }

  /**
   * SATIR DENETİMLERİ — TEK KAYNAK (ayna kuralı).
   * Masaüstü tablo ve mobil kart AYNI parçaları kullanır: aynı id'ler, aynı aria etiketleri,
   * aynı işleyiciler. Mobil için ayrı bir satır bileşeni yazılsaydı iki ekran sessizce
   * ayrışırdı (bir tarafta düzeltilen doğrulama diğerinde unutulurdu).
   */
  const satir = (row: LineRow, idx: number) => {
    // Anlaşması yok mu → satır/kart kenarına uyarı; metin bilgi şeridinde de yazılı
    // (renk TEK BAŞINA anlam taşımasın, CLAUDE.md ✅#5).
    const needsAgr = orderType === "customer" && !!customerId && !!row.itemId && !linePriced(row) && priceMap[fk(row.itemId)] === false;
    const up = lineUnitPrice(row);
    const lt = lineTotal(row);
    return {
      needsAgr,
      urun: (
        <SearchSelect
          id={fid(`item-${idx}`)}
          value={row.itemId || null}
          options={productOptions}
          onChange={(v) => (v ? pickProduct(idx, v) : setLine(idx, { itemId: "" }))}
          placeholder="— Mamul seç —"
          searchPlaceholder="Mamul adı / kodu ara…"
          emptyText={produciblesQ.isLoading ? "Yükleniyor…" : "Mamul bulunamadı"}
        />
      ),
      miktar: (
        <input
          id={fid(`qty-${idx}`)}
          className="form-input mono" type="number" step="any" min={0} placeholder="0"
          value={row.qty} onChange={(e) => setLine(idx, { qty: e.target.value })}
          inputMode="decimal" aria-label={`${idx + 1}. satır miktar`}
        />
      ),
      birim: (
        <select id={fid(`uom-${idx}`)} className="form-select" value={row.uomId} onChange={(e) => setLine(idx, { uomId: e.target.value })} aria-label={`${idx + 1}. satır birim`}>
          {uomQ.data?.map((u) => <option key={u.id} value={u.id}>{u.symbol}</option>)}
        </select>
      ),
      // Fiyat SALT-OKUNUR: anlaşmadan gelir, elle girilmez (mevcut kural).
      birimFiyat: up ? <>{fmtNum(up.unitPrice)} <span className="unit">{CUR_SYMBOL[up.currency] ?? up.currency}</span></> : <span className="oe-dim">—</span>,
      satirToplami: lt ? <b className="mono">{fmtNum(lt.amount)} {CUR_SYMBOL[lt.currency] ?? lt.currency}</b> : <span className="oe-dim">—</span>,
      kaldir: (
        <button
          type="button" className="iact danger"
          onClick={() => setLines((ls) => ls.filter((_, i) => i !== idx))}
          aria-label={`${idx + 1}. satırı kaldır`} title="Satırı kaldır"
        >
          <Icon name="x" size={14} />
        </button>
      ),
      bilgi: row.itemId ? (
        <>
          <AtpBadge itemId={row.itemId} requestedQty={Number(row.qty) || 0} detay={isMobile} />
          {orderType === "customer" && customerId ? (
            <LineAgreement
              customerId={customerId} itemId={row.itemId}
              frozen={donukGecerli(row) ? row.frozen : null}
              onResolved={onPriceResolved}
              onOpen={(itemId, initial) => setAgr({ itemId, initial })}
            />
          ) : null}
        </>
      ) : null,
    };
  };

  function kalemEkle() {
    const yeniIdx = lines.length;
    setLines((ls) => [...ls, emptyLine(adetUom?.id ?? "")]);
    setOdakSatir(yeniIdx);
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setGonderimNo((n) => n + 1);
    setSubmitErr(null);
    if (orderType === "customer" && !customerId) return setSubmitErr("Müşteri seçin.");
    const valid = lines.filter((l) => l.itemId && l.qty && Number(l.qty) > 0 && l.uomId);
    if (valid.length === 0) return setSubmitErr("En az 1 geçerli satır girin.");
    if (orderType === "customer" && !valid.every(linePriced)) {
      const loading = valid.some((l) => !linePriced(l) && priceMap[fk(l.itemId)] === undefined);
      return setSubmitErr(loading && unpricedNames.length === 0
        ? "Satır fiyatları hâlâ kontrol ediliyor — bir saniye sonra tekrar deneyin."
        : `Anlaşması olmayan satır: ${unpricedNames.join(", ")}. 'Anlaşma Yap' ile fiyat belirleyin.`);
    }

    // Fiyat: mevcut anlaşma varsa GÖNDERME → backend son anlaşmadan dondurur (güncel kalır). Anlaşma YOKSA
    // ama satırın dondurulmuş fiyatı varsa (ürün değişmedi) → onu gönder ki backend korusun (fiyat kaybı/kilit yok).
    const lineInputs = valid.map((l) => {
      const keepFrozen = priceMap[fk(l.itemId)] !== true && donukGecerli(l);
      return {
        itemKind: "finished" as const, itemId: l.itemId, qty: Number(l.qty), uomId: l.uomId,
        ...(keepFrozen
          ? { unitPrice: l.frozen!.unitPrice, currency: l.frozen!.currency as "TRY" | "USD" | "EUR" | "GBP", exchangeRate: l.frozen!.exchangeRate ?? undefined }
          : {}),
      };
    });
    try {
      if (isEdit) {
        await updateMut.mutateAsync({
          id: orderId!,
          customerId: orderType === "customer" ? customerId : null,
          requestedDate: requestedDate ? new Date(requestedDate).toISOString() : null,
          promisedDate: promisedDate ? new Date(promisedDate).toISOString() : null,
          notes: notes || null,
          lines: lineInputs,
        });
        // Kayıt TAMAM. Tazeleme sürerken düğmeler kilitli kalsın (çift gönderim) ve çıkış koruması
        // "kaydedilmedi" diye YANLIŞ uyarmasın.
        kasitliCikis.current = true;
        setKaydedildi(true);
        await qc.invalidateQueries();
        onClose();
      } else {
        const res = await createMut.mutateAsync({
          orderType,
          customerId: orderType === "customer" ? customerId : undefined,
          requestedDate: requestedDate ? new Date(requestedDate).toISOString() : undefined,
          promisedDate: promisedDate ? new Date(promisedDate).toISOString() : undefined,
          notes: notes || undefined,
          lines: lineInputs,
        });
        kasitliCikis.current = true;
        setKaydedildi(true);
        await qc.invalidateQueries();
        setDoneCode(res.code);
      }
    } catch {
      /* err banner */
    }
  }

  return (
    <>
    <Shell
      open={open}
      onClose={kapat}
      // Çekmece yerleşiminde de kalem tablosu 7 sütun taşıyor: 720px'te "Birim Fiyat" ve
      // "Satır Toplamı" kırpılıyordu → lg (720) yerine 3xl (1080, +%50).
      size="3xl"
      // Sayfa yerleşiminde kalem tablosu 7 sütun taşıyor; 980px'lik varsayılan gövde
      // sınırında "Birim Fiyat" ve "Satır Toplamı" sıkışıyordu.
      wide={isPage}
      title={isEdit ? "Sipariş Düzenle" : "Yeni Satış Siparişi"}
      subtitle={isEdit ? "Satır/miktar düzenlenir; fiyat son anlaşmadan gelir" : "Belge taslak olarak kaydedilir, onay listeden yapılır"}
      footer={
        doneCode ? (
          <button type="button" className="btn-md primary" onClick={kapat}>Kapat</button>
        ) : (
          <>
            {/* Referanstaki eylem sırası: kapat (ghost) → kaydet (primary).
                "Onaya Gönder" YOK: yeni siparişte onaya gönderme akışı bu sistemde
                bulunmuyor — onay, liste ekranındaki "Onayla" ile yapılıyor. */}
            <button type="button" className="btn-md ghost" onClick={kapat} disabled={pending}>
              {/* Etiket sayfada da "İptal" — kod tabanının iptal sözcüğü bu (74 kullanım:
                  Vazgeç/İptal/Kapat). Sekmeyi kapatan başka bir düğme icat etmiyoruz;
                  sekmenin kendi × 'i ve başlıktaki "Kapat" zaten var. */}
              İptal
            </button>
            {/* Fiyat kapısı YALNIZ müşteri seçiliyken uygulanır. Müşteri yokken fiyat HİÇ
                sorgulanmaz (anlaşma müşteriye bağlı) → kapı sonsuza dek kapalı kalıyor, düğme pasif
                ve altındaki not "fiyatlar kontrol ediliyor…" diyordu; mobilde kullanıcı pasif
                düğmeye basıp hiçbir tepki alamıyordu. Artık düğme basılır ve gönderim "Müşteri
                seçin." hatasını görünür alana getirir. */}
            <button type="submit" form={fid("form")} className="btn-md primary" disabled={pending || (orderType === "customer" && !!customerId && validLines.length > 0 && !allPriced)}>
              <Icon name="check" size={14} />
              {pending ? "Kaydediliyor…" : isEdit ? "Güncelle" : "Taslak Kaydet"}
            </button>
          </>
        )
      }
    >
      {doneCode ? (
        <div className="form-banner-ok">
          <div>
            <div className="oe-done-title">Sipariş oluşturuldu (taslak)</div>
            <div><span className="mono oe-done-code">{doneCode}</span></div>
            <div className="oe-done-note">Listeden "Onayla" ile stok rezervasyonu yapılır (ATP'ye göre).</div>
          </div>
        </div>
      ) : (
        <form id={fid("form")} className="oe-form" onSubmit={handleSubmit} noValidate>
          {/* key=gonderimNo: aynı metin tekrar oluşsa da bant YENİDEN MONTE edilir → ekran okuyucu
              `role="alert"`ı yeniden duyurur (metin değişmezse duyuru yapılmaz). */}
          {errorMessage ? <div key={gonderimNo} ref={hataRef} className="form-banner-err oe-err" role="alert">{errorMessage}</div> : null}

          {/* ══ BELGE KÜNYESİ + DURUM AKIŞI ══
              Yalnız sayfa yerleşiminde: çekmecenin kendi başlığı zaten künye görevi görüyor. */}
          {isPage ? (
            <DocHeader
              /* Kod SUNUCUDA üretiliyor (SO-YYYY-NNNNN); yeni belgede henüz yok. */
              code={isEdit ? orderQ.data?.code : null}
              pending="Kod kaydedilince atanır"
              kind={<>{orderType === "customer" ? "Müşteri Siparişi" : "Stoğa Üretim"} · {AKIS.find((a) => a.key === aktifDurum)?.label}</>}
              meta={[olusturan ? `Oluşturan ${olusturan}` : null, new Date().toLocaleDateString("tr-TR"), aktifTesis]}
              right={
                <ol className="doc-steps" aria-label="Sipariş durumu">
                  {AKIS.map((a) => (
                    <li key={a.key} className={`doc-step${a.key === aktifDurum ? " on" : ""}`} aria-current={a.key === aktifDurum ? "step" : undefined}>
                      <span className="dot" aria-hidden="true" />{a.label}
                    </li>
                  ))}
                </ol>
              }
            />
          ) : null}

          {/* ══ ALAN ŞERİDİ — referanstaki tek satır düzeni ══ */}
          <div className="oe-fields">
            <div className="form-field">
              <span className="lbl">Sipariş Tipi</span>
              <div className="form-radio-group" role="radiogroup">
                <label className="form-radio">
                  <input type="radio" name={fid("otype")} checked={orderType === "customer"} onChange={() => setOrderType("customer")} />
                  <span>Müşteri</span>
                </label>
                <label className="form-radio">
                  <input type="radio" name={fid("otype")} checked={orderType === "stock_replenish"} onChange={() => setOrderType("stock_replenish")} />
                  <span>Stoğa Üretim</span>
                </label>
              </div>
            </div>
            <div className="form-field">
              <label className="lbl" htmlFor={fid("cust")}>Müşteri {orderType === "customer" ? <span className="req">*</span> : null}</label>
              <SearchSelect
                id={fid("cust")}
                value={orderType === "customer" ? (customerId || null) : null}
                options={customerOptions}
                onChange={(v) => setCustomerId(v ?? "")}
                placeholder={orderType === "customer" ? "— Seçin —" : "(stoğa üretim)"}
                searchPlaceholder="Müşteri adı / kodu ara…"
                emptyText="Müşteri bulunamadı"
                disabled={orderType !== "customer"}
              />
            </div>
            <div className="form-field">
              <label className="lbl" htmlFor={fid("date")}>İstenen Tarih</label>
              <DateField id={fid("date")} value={requestedDate} onChange={setRequestedDate} aria-label="İstenen tarih" />
            </div>
            {/* Söz verilen (teslim) tarih: sipariş detayında hep gösteriliyordu ama hiçbir
                ekrandan YAZILAMIYORDU → kalıcı olarak "—" kalıyordu. Artık düzenlenebilir. */}
            <div className="form-field">
              <label className="lbl" htmlFor={fid("pdate")}>Söz Verilen Teslim</label>
              <DateField id={fid("pdate")} value={promisedDate} onChange={setPromisedDate} aria-label="Söz verilen teslim tarihi" />
            </div>
          </div>

          {/* ══ KALEM TABLOSU — referanstaki sütun düzeni, bizim .dt2 tablomuzla ══ */}
          <section className="d-card oe-lines">
            <div className="d-card-head">
              <div className="left"><span className="t">Sipariş Satırları</span><span className="s">mamul</span></div>
            </div>
            <div className="d-card-body flush">
              {isMobile ? (
                /* ══ MOBİL: kalem kartları ══ */
                <div className="oe-mlines">
                  {lines.map((row, idx) => {
                    const c = satir(row, idx);
                    return (
                      <div key={idx} id={fid(`mline-${idx}`)} className={`oe-mline${c.needsAgr ? " warn" : ""}`}>
                        <div className="oe-mline-head">
                          <span className="oe-mline-no">{idx + 1}</span>
                          <div className="oe-mline-item">{c.urun}</div>
                          {c.kaldir}
                        </div>
                        <div className="oe-mline-qty">
                          <div className="form-field">
                            <label className="lbl" htmlFor={fid(`qty-${idx}`)}>Miktar</label>
                            {c.miktar}
                          </div>
                          <div className="form-field">
                            <label className="lbl" htmlFor={fid(`uom-${idx}`)}>Birim</label>
                            {c.birim}
                          </div>
                        </div>
                        {row.itemId ? (
                          <>
                            <dl className="oe-mline-price">
                              <div><dt>Birim fiyat</dt><dd>{c.birimFiyat}</dd></div>
                              <div><dt>Satır toplamı</dt><dd>{c.satirToplami}</dd></div>
                            </dl>
                            <div className="oe-mline-info">{c.bilgi}</div>
                          </>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              ) : (
              <table className="dt2">
                <thead>
                  <tr>
                    <th style={{ width: 34 }}>#</th>
                    <th>Ürün</th>
                    <th className="num" style={{ width: 96 }}>Miktar</th>
                    <th style={{ width: 84 }}>Birim</th>
                    <th className="num" style={{ width: 128 }}>Birim Fiyat</th>
                    <th className="num" style={{ width: 140 }}>Satır Toplamı</th>
                    <th style={{ width: 40 }} aria-label="Kaldır" />
                  </tr>
                </thead>
                <tbody>
                  {lines.map((row, idx) => {
                    const c = satir(row, idx);
                    return (
                      <Fragment key={idx}>
                        <tr className={`${row.itemId ? "oe-mainrow " : ""}${c.needsAgr ? "warn" : ""}`.trim() || undefined}>
                          <td className="oe-row-no">{idx + 1}</td>
                          <td className="oe-cell-item">{c.urun}</td>
                          <td>{c.miktar}</td>
                          <td>{c.birim}</td>
                          <td className="cell-num">{c.birimFiyat}</td>
                          <td className="cell-num">{c.satirToplami}</td>
                          <td>{c.kaldir}</td>
                        </tr>
                        {row.itemId ? (
                          <tr className={`oe-subrow${c.needsAgr ? " warn" : ""}`}>
                            <td colSpan={7}>
                              <div className="oe-subrow-in">{c.bilgi}</div>
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
              )}
            </div>
            <div className="oe-lines-foot">
              <button type="button" className="btn-md secondary sm" onClick={kalemEkle}>
                <Icon name="plus" size={14} />Kalem Ekle
              </button>
              <span className="help">Fiyat, müşterinin son anlaşmasından gelir — elle girilmez.</span>
            </div>
          </section>

          {orderType === "customer" && validLines.length > 0 && !allPriced ? (
            <div className={unpricedNames.length > 0 ? "form-banner-warn oe-gap" : "help oe-gap"}>
              {unpricedNames.length > 0 ? (
                <><Icon name="alertTriangle" size={14} />Anlaşması olmayan satır{unpricedNames.length > 1 ? "lar" : ""}: <b>{unpricedNames.join(", ")}</b> — ilgili satırda "Anlaşma Yap" ile fiyat belirleyin.</>
              ) : !customerId ? (
                "Önce müşteri seçin — satır fiyatları müşterinin anlaşmasından gelir."
              ) : pricesLoading ? (
                "Satır fiyatları kontrol ediliyor…"
              ) : (
                'Anlaşması olmayan satır var — "Anlaşma Yap" ile fiyat belirleyin.'
              )}
            </div>
          ) : null}

          {/* ══ ALT BLOK: solda ham madde ihtiyacı, sağda toplam ══ */}
          <div className="oe-bottom">
            <div>
              {/* Canlı eksik ham madde tablosu — geçerli satırların üretimi için */}
              <RawMaterialNeeds orderId={orderId ?? null} lines={lines.filter((l) => l.itemId && Number(l.qty) > 0).map((l) => ({ itemId: l.itemId, qty: Number(l.qty) }))} />
            </div>
            <div className="oe-totals">
              {totalsByCurrency.length === 0 ? (
                <div className="oe-tot-row"><span>Ara toplam</span><b>—</b></div>
              ) : (
                totalsByCurrency.map((t, i) => (
                  <div key={t.currency} className={i === totalsByCurrency.length - 1 ? "oe-tot-row grand" : "oe-tot-row"}>
                    <span>{totalsByCurrency.length > 1 ? `Ara toplam (${t.currency})` : "Ara toplam"}</span>
                    <b>{fmtNum(t.amount)} {CUR_SYMBOL[t.currency] ?? t.currency}</b>
                  </div>
                ))
              )}
              {/* SESSİZ EKSİK YOK: fiyatı çözülemeyen satır toplama girmiyorsa söylenir. */}
              {fiyatsizSatir > 0 ? (
                <p className="oe-tot-note">{fiyatsizSatir} satırın fiyatı yok — toplama dahil edilmedi.</p>
              ) : null}
              {totalsByCurrency.length > 1 ? (
                <p className="oe-tot-note">Farklı para birimleri ayrı gösterilir; toplanmaz.</p>
              ) : null}
            </div>
          </div>

          {/* ══ KAYIT SONRASI NE OLACAK — referanstaki bilgi kartının bizdeki gerçek karşılığı ══
              Buradaki maddeler sistemin GERÇEK davranışıdır; süsleme değildir. */}
          <section className="d-card oe-gap">
            <div className="d-card-head"><div className="left"><span className="t">Kayıt Sonrası</span><span className="s">ne olacak</span></div></div>
            <div className="d-card-body">
              <div className="oe-after-row">
                <span className="ic"><Icon name="check" size={14} /></span>
                <span className="grow">Sipariş <b>taslak</b> olarak kaydedilir</span>
                <span className="meta">durum: Taslak</span>
              </div>
              <div className="oe-after-row">
                <span className="ic off"><Icon name="chevRight" size={14} /></span>
                <span className="grow">Listeden <b>Onayla</b> denince stok rezervasyonu yapılır</span>
                <span className="meta">ATP'ye göre</span>
              </div>
              <div className="oe-after-row">
                <span className="ic off"><Icon name="chevRight" size={14} /></span>
                <span className="grow">Stok yetmeyen satırlar için <b>iş emri</b> açılır</span>
                <span className="meta">reçeteden</span>
              </div>
            </div>
          </section>

          <div className="form-field oe-gap">
            <label className="lbl" htmlFor={fid("notes")}>Notlar</label>
            <textarea id={fid("notes")} className="form-textarea" value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={1000} rows={2} />
          </div>
        </form>
      )}
    </Shell>

    {isMobile && open && !doneCode ? <KayitsizCikisKorumasi kirli={kirli} kasitli={kasitliCikis} /> : null}

    {customerId ? (
      <PriceAgreementDrawer
        open={!!agr}
        customerId={customerId}
        customerName={customerName}
        itemKind="finished"
        itemId={agr?.itemId}
        itemLabel={agrProduct ? `${agrProduct.code} — ${agrProduct.name}` : undefined}
        initialPrice={agr?.initial?.unitPrice ?? null}
        initialCurrency={agr?.initial?.currency ?? null}
        initialRate={agr?.initial?.exchangeRate ?? null}
        onClose={() => setAgr(null)}
        onCreated={() => { qc.invalidateQueries(); }}
      />
    ) : null}
    </>
  );
}
